import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import DonorAuthModal from "./DonorAuthModal";
import {
  clearDonorToken,
  donorSecureFetch,
  getDonorToken,
} from "../data/donorAuth";

const {
  formatCentsToDollars,
  formatDateTimeToLocalString,
} = require("../store/calc-helpers");

const donationStatusBadge = (status = "") => {
  const value = `${status}`.toLowerCase();
  let color = "gray";
  if (value === "succeeded" || value === "completed") color = "green";
  else if (value === "pending" || value === "pending_ach") color = "blue";
  else if (value === "canceled" || value === "cancelled") color = "orange";
  else if (value === "failed" || value === "declined") color = "red";
  return <Badge colorScheme={color}>{status || "Unknown"}</Badge>;
};

const subscriptionStatusMeta = (status = "") => {
  const value = `${status}`.toLowerCase();
  if (!value) {
    return { label: "Unknown", color: "gray" };
  }
  if (value === "canceled" || value === "cancelled") {
    return { label: "Canceled", color: "orange" };
  }
  if (value === "paused") {
    return { label: "Paused", color: "orange" };
  }
  if (value === "past_due") {
    return { label: "Past Due", color: "orange" };
  }
  if (value === "incomplete" || value === "unpaid") {
    return { label: "Pending", color: "yellow" };
  }
  if (value === "failed" || value === "declined") {
    return { label: "Failed", color: "red" };
  }
  return { label: "Active", color: "green" };
};

const subscriptionStatusBadge = (status = "") => {
  const meta = subscriptionStatusMeta(status);
  return <Badge colorScheme={meta.color}>{meta.label}</Badge>;
};

const paymentMethodStatusBadge = (status = "") => {
  const value = `${status}`.toUpperCase();
  if (value === "NEEDS_UPDATE") {
    return <Badge colorScheme="red">Needs update</Badge>;
  }
  if (value === "PENDING") {
    return <Badge colorScheme="blue">Pending</Badge>;
  }
  return <Badge colorScheme="green">Active</Badge>;
};

const normalizePaymentMethodStatus = (method = {}) => {
  const explicit = `${method.status || ""}`.trim().toUpperCase();
  if (explicit === "NEEDS_UPDATE") return "NEEDS_UPDATE";
  if (explicit === "PENDING") return "PENDING";
  if (explicit === "ACTIVE") return "ACTIVE";

  const lastStatus = `${method.last_status || ""}`.trim().toUpperCase();
  if (method.needs_update || lastStatus === "FAILED") return "NEEDS_UPDATE";
  if (lastStatus === "PENDING") return "PENDING";
  return "ACTIVE";
};

const normalizeSavedPaymentMethod = (method = {}) => {
  const id = method.id ?? method.key;
  if (id === null || id === undefined || id === "") return null;

  return {
    ...method,
    id,
    last_used_at: method.last_used_at || method.latest_used_at || null,
    last_status: method.last_status || "N/A",
    status: normalizePaymentMethodStatus(method),
  };
};

function DonorAccount() {
  const toast = useToast();
  const authModal = useDisclosure();
  const [loading, setLoading] = useState(true);
  const [donor, setDonor] = useState(null);
  const [history, setHistory] = useState(null);
  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
    saveProfileForFuture: false,
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [paymentNicknameDrafts, setPaymentNicknameDrafts] = useState({});
  const [savingPaymentMethodId, setSavingPaymentMethodId] = useState(null);
  const [removingPaymentMethodId, setRemovingPaymentMethodId] = useState(null);

  const applyDonorToProfileForm = useCallback((profile) => {
    setProfileForm({
      firstName: profile?.first_name || "",
      lastName: profile?.last_name || "",
      addressLine1: profile?.address_line_1 || "",
      addressLine2: profile?.address_line_2 || "",
      city: profile?.city || "",
      state: profile?.state || "",
      zipCode: profile?.zip_code || "",
      saveProfileForFuture: !!profile?.save_profile_for_future,
    });
  }, []);

  const loadDonorData = useCallback(async () => {
    const token = getDonorToken();
    if (!token) {
      setDonor(null);
      setHistory(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [meResp, historyResp] = await Promise.all([
        donorSecureFetch("/api/donor-auth/me"),
        donorSecureFetch("/api/donor-auth/history"),
      ]);

      if (!meResp.ok) {
        throw new Error(meResp.message || "Failed to load donor account");
      }
      if (!historyResp.ok) {
        throw new Error(historyResp.message || "Failed to load donor history");
      }

      setDonor(meResp.donor || null);
      const normalizedPaymentMethods = Array.isArray(historyResp.paymentMethods)
        ? historyResp.paymentMethods
            .map(normalizeSavedPaymentMethod)
            .filter(Boolean)
        : [];
      setHistory({
        ...historyResp,
        paymentMethods: normalizedPaymentMethods,
      });
      applyDonorToProfileForm(meResp.donor || null);
      const methodDrafts = {};
      normalizedPaymentMethods.forEach((method) => {
        methodDrafts[method.id] = method.nickname || "";
      });
      setPaymentNicknameDrafts(methodDrafts);
    } catch (error) {
      clearDonorToken();
      setDonor(null);
      setHistory(null);
      toast({
        title: "Session expired",
        description: error.message,
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [applyDonorToProfileForm, toast]);

  useEffect(() => {
    loadDonorData();
  }, [loadDonorData]);

  const handleSignOut = () => {
    clearDonorToken();
    setDonor(null);
    setHistory(null);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await donorSecureFetch("/api/donor-auth/me/profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        data: profileForm,
      });
      if (!response.ok) {
        throw new Error(response.message || "Failed to update profile");
      }
      setDonor(response.donor || donor);
      applyDonorToProfileForm(response.donor || donor);
      toast({
        title: "Profile updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Profile update failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePaymentNickname = async (paymentMethodId) => {
    setSavingPaymentMethodId(paymentMethodId);
    try {
      const response = await donorSecureFetch(
        `/api/donor-auth/payment-methods/${paymentMethodId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          data: { nickname: paymentNicknameDrafts[paymentMethodId] || "" },
        },
      );
      if (!response.ok) {
        throw new Error(response.message || "Failed to update payment method");
      }
      await loadDonorData();
      toast({
        title: "Payment method updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Update failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingPaymentMethodId(null);
    }
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    setRemovingPaymentMethodId(paymentMethodId);
    try {
      const response = await donorSecureFetch(
        `/api/donor-auth/payment-methods/${paymentMethodId}`,
        { method: "DELETE" },
      );
      if (!response.ok) {
        throw new Error(response.message || "Failed to remove payment method");
      }
      await loadDonorData();
      toast({
        title: "Payment method removed",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Remove failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRemovingPaymentMethodId(null);
    }
  };

  const summary = history?.summary || {};
  const donations = Array.isArray(history?.donations) ? history.donations : [];
  const subscriptions = Array.isArray(history?.subscriptions)
    ? history.subscriptions
    : [];
  const paymentMethods = Array.isArray(history?.paymentMethods)
    ? history.paymentMethods
    : [];
  const donorName = useMemo(() => {
    if (!donor) return "";
    const full = `${donor.first_name || ""} ${donor.last_name || ""}`.trim();
    return full || donor.email || "Donor";
  }, [donor]);

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" color="orange.400" />
      </Box>
    );
  }

  if (!donor) {
    return (
      <Box p={6} maxW="lg" mx="auto">
        <Heading size="lg" mb={3}>
          Donor Account
        </Heading>
        <Text color="gray.600" mb={5}>
          Sign in to view your donation history, saved payment methods, and subscription status.
        </Text>
        <Button colorScheme="orange" onClick={authModal.onOpen}>
          Sign In / Create Account
        </Button>
        <DonorAuthModal
          isOpen={authModal.isOpen}
          onClose={authModal.onClose}
          onSuccess={() => {
            authModal.onClose();
            loadDonorData();
          }}
          initialMode="login"
        />
      </Box>
    );
  }

  return (
    <Box p={6} maxW="6xl" mx="auto">
      <HStack justify="space-between" mb={4} flexWrap="wrap" gap={3}>
        <Box>
          <Heading size="lg">Donor Account</Heading>
          <Text color="gray.600">
            {donorName} ({donor.email})
          </Text>
        </Box>
        <Button variant="outline" onClick={handleSignOut}>
          Sign Out
        </Button>
      </HStack>

      <Wrap spacing={4} mb={8}>
        <WrapItem>
          <Stat borderWidth="1px" borderRadius="md" p={4} minW="180px">
            <StatLabel>Total Donations</StatLabel>
            <StatNumber>{summary.total_donations || 0}</StatNumber>
          </Stat>
        </WrapItem>
        <WrapItem>
          <Stat borderWidth="1px" borderRadius="md" p={4} minW="230px">
            <StatLabel>Total Succeeded Amount</StatLabel>
            <StatNumber>
              ${formatCentsToDollars(summary.total_succeeded_amount || 0)}
            </StatNumber>
          </Stat>
        </WrapItem>
        <WrapItem>
          <Stat borderWidth="1px" borderRadius="md" p={4} minW="180px">
            <StatLabel>Active Subscriptions</StatLabel>
            <StatNumber>{summary.active_subscriptions || 0}</StatNumber>
          </Stat>
        </WrapItem>
      </Wrap>

      <Heading size="md" mb={3}>
        Profile
      </Heading>
      <Box borderWidth="1px" borderRadius="md" p={4} mb={8}>
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
          <GridItem>
            <FormControl>
              <FormLabel htmlFor="donor-profile-first-name">First Name</FormLabel>
              <Input
                id="donor-profile-first-name"
                value={profileForm.firstName}
                onChange={(e) =>
                  setProfileForm((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel htmlFor="donor-profile-last-name">Last Name</FormLabel>
              <Input
                id="donor-profile-last-name"
                value={profileForm.lastName}
                onChange={(e) =>
                  setProfileForm((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }))
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel htmlFor="donor-profile-address-line-1">
                Address Line 1
              </FormLabel>
              <Input
                id="donor-profile-address-line-1"
                value={profileForm.addressLine1}
                onChange={(e) =>
                  setProfileForm((prev) => ({
                    ...prev,
                    addressLine1: e.target.value,
                  }))
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel htmlFor="donor-profile-address-line-2">
                Address Line 2
              </FormLabel>
              <Input
                id="donor-profile-address-line-2"
                value={profileForm.addressLine2}
                onChange={(e) =>
                  setProfileForm((prev) => ({
                    ...prev,
                    addressLine2: e.target.value,
                  }))
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel htmlFor="donor-profile-city">City</FormLabel>
              <Input
                id="donor-profile-city"
                value={profileForm.city}
                onChange={(e) =>
                  setProfileForm((prev) => ({
                    ...prev,
                    city: e.target.value,
                  }))
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel htmlFor="donor-profile-state">State</FormLabel>
              <Input
                id="donor-profile-state"
                value={profileForm.state}
                onChange={(e) =>
                  setProfileForm((prev) => ({
                    ...prev,
                    state: e.target.value,
                  }))
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel htmlFor="donor-profile-zip-code">Zip Code</FormLabel>
              <Input
                id="donor-profile-zip-code"
                value={profileForm.zipCode}
                onChange={(e) =>
                  setProfileForm((prev) => ({
                    ...prev,
                    zipCode: e.target.value,
                  }))
                }
              />
            </FormControl>
          </GridItem>
        </Grid>
        <Checkbox
          mt={4}
          isChecked={profileForm.saveProfileForFuture}
          onChange={(e) =>
            setProfileForm((prev) => ({
              ...prev,
              saveProfileForFuture: e.target.checked,
            }))
          }
        >
          Save this info for future donations
        </Checkbox>
        <Button
          mt={4}
          colorScheme="orange"
          onClick={handleSaveProfile}
          isLoading={savingProfile}
        >
          Save Profile
        </Button>
      </Box>

      <Heading size="md" mb={3}>
        Payment Methods
      </Heading>
      <Box borderWidth="1px" borderRadius="md" p={4} mb={8}>
        {paymentMethods.length === 0 ? (
          <Text color="gray.500">No saved payment methods yet.</Text>
        ) : (
          paymentMethods.map((method) => {
            const nicknameInputId = `donor-payment-nickname-${method.id}`;
            return (
              <Box key={method.id} borderWidth="1px" borderRadius="md" p={3} mb={3}>
                <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
                  <Text fontWeight="semibold">
                    {method.card_type || method.payment_method || "Payment Method"}
                  </Text>
                  {paymentMethodStatusBadge(method.status)}
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  Last used:{" "}
                  {method.last_used_at
                    ? formatDateTimeToLocalString(method.last_used_at)
                    : "N/A"}
                </Text>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Last status: {method.last_status || "N/A"}
                </Text>
                <HStack align="end" gap={2} flexWrap="wrap">
                  <FormControl maxW="260px">
                    <FormLabel htmlFor={nicknameInputId} fontSize="sm" mb={1}>
                      Nickname
                    </FormLabel>
                    <Input
                      id={nicknameInputId}
                      size="sm"
                      value={paymentNicknameDrafts[method.id] || ""}
                      onChange={(e) =>
                        setPaymentNicknameDrafts((prev) => ({
                          ...prev,
                          [method.id]: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSavePaymentNickname(method.id)}
                    isLoading={savingPaymentMethodId === method.id}
                  >
                    Update
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => handleRemovePaymentMethod(method.id)}
                    isLoading={removingPaymentMethodId === method.id}
                  >
                    Remove
                  </Button>
                </HStack>
              </Box>
            );
          })
        )}
      </Box>

      <Heading size="md" mb={3}>
        Donation History
      </Heading>
      <Box borderWidth="1px" borderRadius="md" overflowX="auto" mb={8}>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th>Organization</Th>
              <Th>Campaign/Fund</Th>
              <Th>Status</Th>
              <Th isNumeric>Donation</Th>
              <Th isNumeric>Tip</Th>
            </Tr>
          </Thead>
          <Tbody>
            {donations.length === 0 && (
              <Tr>
                <Td colSpan={6}>
                  <Text color="gray.500">No donations found.</Text>
                </Td>
              </Tr>
            )}
            {donations.map((row) => (
              <Tr key={`donor-donation-${row.id}`}>
                <Td>{formatDateTimeToLocalString(row.created_at)}</Td>
                <Td>{row.organization_name || "N/A"}</Td>
                <Td>{row.campaign_name || row.fund_group_title || "N/A"}</Td>
                <Td>{donationStatusBadge(row.status)}</Td>
                <Td isNumeric>${formatCentsToDollars(row.donation_amount || 0)}</Td>
                <Td isNumeric>
                  ${formatCentsToDollars(row.platform_tip_amount || 0)}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Heading size="md" mb={3}>
        Subscriptions
      </Heading>
      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Box display={{ base: "none", md: "block" }} overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Amount</Th>
                <Th>Frequency</Th>
                <Th>Next Billing Date</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {subscriptions.length === 0 && (
                <Tr>
                  <Td colSpan={4}>
                    <Text color="gray.500">No subscriptions found.</Text>
                  </Td>
                </Tr>
              )}
              {subscriptions.map((row) => (
                <Tr key={`donor-sub-${row.id}`}>
                  <Td>${formatCentsToDollars(row.donation_amount || 0)}</Td>
                  <Td>{row.donation_type || "N/A"}</Td>
                  <Td>
                    {row.next_donation_date
                      ? formatDateTimeToLocalString(row.next_donation_date)
                      : "N/A"}
                  </Td>
                  <Td>{subscriptionStatusBadge(row.status)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        <Box display={{ base: "block", md: "none" }}>
          {subscriptions.length === 0 ? (
            <Text color="gray.500">No subscriptions found.</Text>
          ) : (
            subscriptions.map((row) => (
              <Box
                key={`donor-sub-mobile-${row.id}`}
                borderWidth="1px"
                borderRadius="md"
                p={3}
                mb={3}
              >
                <Text fontSize="sm" color="gray.600">
                  Amount
                </Text>
                <Text mb={2}>${formatCentsToDollars(row.donation_amount || 0)}</Text>
                <Text fontSize="sm" color="gray.600">
                  Frequency
                </Text>
                <Text mb={2}>{row.donation_type || "N/A"}</Text>
                <Text fontSize="sm" color="gray.600">
                  Next Billing Date
                </Text>
                <Text mb={2}>
                  {row.next_donation_date
                    ? formatDateTimeToLocalString(row.next_donation_date)
                    : "N/A"}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Status
                </Text>
                <Box mt={1}>{subscriptionStatusBadge(row.status)}</Box>
              </Box>
            ))
          )}
        </Box>
      </Box>

      <Text mt={6} fontSize="sm" color="gray.600">
        Want to donate now?{" "}
        <Link to="/" style={{ color: "#3182ce" }}>
          Back to donation pages
        </Link>
      </Text>

      <DonorAuthModal
        isOpen={authModal.isOpen}
        onClose={authModal.onClose}
        onSuccess={() => {
          authModal.onClose();
          loadDonorData();
        }}
        initialMode="login"
      />
    </Box>
  );
}

export default DonorAccount;
